import classNames from "classnames";
import { ActionIcon } from "PFComponents/action_icon";
import { ActionDiv, Inline, Stack } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { Pill } from "PFComponents/pill";
import { Typography } from "PFComponents/typography";
import { useNotificationMarkAsRead } from "PFCore/hooks/queries/notifications/use_notification_mark_as_read";
import { useRelativeDateFormat } from "PFCore/hooks/use_relative_date_format";
import spacing from "PFTheme/tokens/spacing";
import { Notification, NotificationStatus } from "PFTypes";
import { memo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import css from "./notification_item.module.scss";
import { NotificationButtons } from "./parts/notification_buttons";
import { NotificationMetadata } from "./parts/notification_metadata";

const DOT_SIZE = 8;
const DOT_ORIGINAL_SIZE = 20;
const DOT_VIEW_BOX = `${(DOT_ORIGINAL_SIZE - DOT_SIZE) / 2} 0 ${DOT_SIZE} ${DOT_ORIGINAL_SIZE}`;

type NotificationItemProps = {
  notification: Notification;
};

const NotificationItemComponent = ({ notification }: NotificationItemProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "notifications" });
  const { mutate: markAsRead, isPending: isLoading } = useNotificationMarkAsRead();
  const relativeDateFormat = useRelativeDateFormat();
  const navigate = useNavigate();
  const primaryActionRef = useRef<HTMLButtonElement>(null);

  const { id, title, createdAt, body, status, urlAddress, metadata } = notification;
  const isUnread = status === NotificationStatus.Unread;
  const isImportant = metadata?.important;

  const handleMarkAsRead = useCallback(
    (notificationStatus?: NotificationStatus) => {
      markAsRead({
        id,
        status: notificationStatus || (isUnread ? NotificationStatus.Read : NotificationStatus.Unread)
      });
    },
    [id, isUnread, markAsRead]
  );

  const handleNotificationClick = useCallback(() => {
    if (primaryActionRef.current) {
      primaryActionRef.current.click();
    } else if (urlAddress) {
      navigate(urlAddress);
    }
    handleMarkAsRead(NotificationStatus.Read);
  }, [urlAddress, navigate, handleMarkAsRead]);

  const handleMarkAsReadClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleMarkAsRead();
    },
    [handleMarkAsRead]
  );

  return (
    <ActionDiv className={css.notificationItem} tabIndex={0} onClick={handleNotificationClick}>
      <Stack className={css.notificationContent} style={{ paddingLeft: DOT_SIZE }}>
        {isImportant && <Pill kind="danger" text={t("important")} small />}
        <Inline spread gap="spacingSm">
          <Icon
            name="dot"
            className={classNames({ [css.hiddenDot]: !isUnread })}
            width={DOT_SIZE}
            viewBox={DOT_VIEW_BOX}
            style={{ minWidth: DOT_SIZE, marginLeft: `calc(-${DOT_SIZE}px - ${spacing.spacingSm})` }} // margin to offset the dot to the left of the notification content
          />
          <Typography variant={isUnread ? "bodyBold" : "bodyRegular"}>{title}</Typography>
          <ActionIcon
            classes={{ root: css.actionIcon }}
            name={isUnread ? "unseen" : "mail"}
            aria-label={isUnread ? t("markAsRead") : t("markAsUnread")}
            onClick={handleMarkAsReadClick}
            onKeyDown={(event) => event.stopPropagation()}
            disabled={isLoading}
            size="sm"
          />
        </Inline>
        <Typography variant="labelRegular">{body}</Typography>
        <NotificationMetadata notification={notification} />
        <NotificationButtons
          notification={notification}
          primaryActionRef={primaryActionRef}
          onButtonClick={() => handleMarkAsRead(NotificationStatus.Read)}
        />
        <Typography variant="labelRegular" className={css.notificationLabel}>
          {relativeDateFormat(createdAt)}
        </Typography>
      </Stack>
    </ActionDiv>
  );
};

export const NotificationItem = memo(NotificationItemComponent);
